import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, RefreshCcw, Replace, Archive } from "lucide-react";
import { toast } from "sonner";
import api from "../../console/api";

const btn = "inline-flex items-center gap-1.5 h-9 px-3 rounded text-[10px] font-semibold uppercase tracking-[0.18em] disabled:opacity-40";

export function AuditSnapshots({ rowCount, onChanged }) {
  const [snaps, setSnaps] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/tuber-audit/snapshots").then((r) => setSnaps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const complete = async () => {
    if (!name.trim()) return toast.error("Give this audit a name first (e.g. 'Autumn 2026 lift').");
    if (!window.confirm(`Mark the audit 100% complete and save it as “${name.trim()}”?`)) return;
    setBusy("complete");
    try { await api.post("/admin/tuber-audit/complete", { name: name.trim() }); toast.success("Audit saved and marked complete."); setName(""); await load(); onChanged && await onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the audit."); } finally { setBusy(""); }
  };
  const startNew = async () => {
    if (!window.confirm("Start a brand-new audit? The current counted list is cleared (your saved audits are kept).")) return;
    setBusy("new");
    try { await api.post("/admin/tuber-audit/new"); toast.success("New audit started."); await load(); onChanged && await onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start a new audit."); } finally { setBusy(""); }
  };
  const applyCatalog = async (s) => {
    if (!window.confirm(`Replace the public catalogue with “${s.name}”?\n\nEvery variety in this audit is published (new ones created, existing photos kept). Every other variety is unpublished , nothing is deleted.`)) return;
    setBusy(s.id);
    try { const r = await api.post(`/admin/tuber-audit/snapshots/${s.id}/apply-to-catalog`); toast.success(`Catalogue updated: ${r.data.published} published (${r.data.created} new), ${r.data.unpublished} hidden.`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the catalogue."); } finally { setBusy(""); }
  };

  return (
    <section className="bg-white border border-[#E8DFCB] rounded p-4 space-y-3" data-testid="audit-snapshots">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#7A6A55]">Finished counting? Name this audit</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 2026 Autumn lift , final" data-testid="audit-complete-name"
            className="h-9 px-2 border rounded text-sm bg-white border-[#E8DFCB] w-full" />
        </div>
        <button type="button" onClick={complete} disabled={busy !== "" || rowCount === 0} className={btn + " text-white"} style={{ backgroundColor: "#2F6B4F" }} data-testid="audit-complete">
          {busy === "complete" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Mark 100% complete & save
        </button>
        <button type="button" onClick={startNew} disabled={busy !== ""} className={btn + " border"} style={{ borderColor: "#A33030", color: "#A33030" }} data-testid="audit-new">
          {busy === "new" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />} Start new audit
        </button>
      </div>
      {snaps.length > 0 && (
        <ul className="divide-y divide-[#F3EBDD]" data-testid="audit-snapshot-list">
          {snaps.map((s) => (
            <li key={s.id} className="py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`audit-snapshot-${s.id}`}>
              <Archive className="w-4 h-4 text-[#6E1E3A]" />
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold text-[#3C2A1E]">{s.name}</div>
                <div className="text-[11px] text-[#7A6A55]">Completed {new Date(s.completed_at).toLocaleString("en-NZ")} · {s.total_varieties} varieties · {s.total_tubers} tubers{s.applied_to_catalog_at ? ` · applied to catalogue ${new Date(s.applied_to_catalog_at).toLocaleDateString("en-NZ")}` : ""}</div>
              </div>
              <button type="button" onClick={() => applyCatalog(s)} disabled={busy !== ""} className={btn + " text-white"} style={{ backgroundColor: "#3E1F46" }} data-testid={`audit-apply-catalog-${s.id}`}>
                {busy === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Replace className="w-3.5 h-3.5" />} Replace catalogue with this audit
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
